import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { RotateCcw, Wifi, WifiOff, ChevronRight, CheckCircle2, XCircle, HelpCircle, Loader2, VolumeX, Clock, Lock, Download, Plus, Paintbrush, Sparkles } from 'lucide-react';
import { getSessionId } from '../../lib/sessionManager';
import { createRoom, closeRoom, getStaffingQuestions } from '../../lib/staffingApi';
import { StaffingSocket } from '../../lib/staffingSocket';
import AslSphere from '../../components/staffing/AslSphere';
import ResultScreen from '../../components/staffing/ResultScreen';
import AppFooter from '../../components/AppFooter';
import UpgradeModal from '../../components/billing/UpgradeModal';
import PlanBanner from '../../components/billing/PlanBanner';
import AnalyticsCard from '../../components/staffing/AnalyticsCard';
import { useMockPlan, IS_DEV_BUILD, toggleMockPlan } from '../../lib/mockPlan';
import '../../styles/staffing-theme.css';

const ANSWER_ICON = {
  yes: { Icon: CheckCircle2, color: '#10F2A0', label: 'YES' },
  no: { Icon: XCircle, color: '#FF3B5C', label: 'NO' },
  unsure: { Icon: HelpCircle, color: '#FF9F1C', label: 'UNSURE' },
};

const TTS_SUPPORTED = typeof window !== 'undefined' && 'speechSynthesis' in window;

/** Speak the candidate's answer aloud in en-US. Web Speech API queues internally. */
function speakAnswer(answer) {
  if (!TTS_SUPPORTED || !answer) return;
  try {
    const u = new SpeechSynthesisUtterance(String(answer).toUpperCase());
    u.lang = 'en-US';
    u.rate = 0.95;
    u.pitch = 1.0;
    u.volume = 1.0;
    window.speechSynthesis.speak(u);
  } catch {
    /* noop */
  }
}

export default function RecruiterDashboard() {
  // Set document title
  useEffect(() => {
    const prev = document.title;
    document.title = 'Talent Connect — SignVoice';
    return () => { document.title = prev; };
  }, []);

  // Unlock speechSynthesis on the first user gesture (iOS Safari requirement).
  // The recruiter will click *something* (New Room, a question card, etc.) — that
  // first click is enough to authorize subsequent .speak() calls.
  useEffect(() => {
    if (!TTS_SUPPORTED) return undefined;
    const unlock = () => {
      try {
        const u = new SpeechSynthesisUtterance('');
        u.volume = 0;
        window.speechSynthesis.speak(u);
      } catch { /* noop */ }
    };
    document.addEventListener('click', unlock, { once: true });
    return () => document.removeEventListener('click', unlock);
  }, []);

  const [recruiterSessionId] = useState(() => getSessionId());
  const [room, setRoom] = useState(null); // {room_id, candidate_path, ...}
  const [questions, setQuestions] = useState([]);
  const [wsState, setWsState] = useState('disconnected'); // disconnected|connecting|open|closed
  const [candidateConnected, setCandidateConnected] = useState(false);
  const [pending, setPending] = useState(null); // question_id awaiting answer
  const [answers, setAnswers] = useState([]); // [{question_id, answer, sequence_num}]
  const [result, setResult] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const sockRef = useRef(null);
  // Phase 11 — monotonic counter bumped on WS session:complete so the
  // AnalyticsCard can debounce-refetch immediately after a room finishes.
  const [analyticsRefreshSignal, setAnalyticsRefreshSignal] = useState(0);

  // Phase 7 — frontend-only billing mock. Stripe NOT wired.
  // Phase 15 — `unlimitedOverride` flips the Free tier to unlimited for demos.
  const { plan, used, increment, isAtLimit, unlimitedOverride } = useMockPlan();
  const [upgradeModal, setUpgradeModal] = useState({ open: false, feature: 'rooms_limit', variant: 'soft' });
  const openUpgrade = useCallback((feature, variant = 'soft') => {
    setUpgradeModal({ open: true, feature, variant });
  }, []);
  const closeUpgrade = useCallback(() => {
    setUpgradeModal((m) => ({ ...m, open: false }));
  }, []);

  // Fetch questions catalogue once
  useEffect(() => {
    getStaffingQuestions().then(setQuestions).catch((e) => setError(String(e)));
  }, []);

  // Initial room creation. useRef-guarded so React.StrictMode (dev) does NOT
  // double-fire handleNewRoom (which would double-increment the mocked quota).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) return;
    didMountRef.current = true;
    handleNewRoom();
    return () => {
      try { sockRef.current?.close(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wireSocket = useCallback((roomId) => {
    if (sockRef.current) {
      try { sockRef.current.close(); } catch { /* noop */ }
    }
    const sock = new StaffingSocket(roomId, 'recruiter', recruiterSessionId);
    sockRef.current = sock;
    setWsState('connecting');

    sock.on('open', () => setWsState('open'));
    sock.on('close', () => setWsState('closed'));
    sock.on('error', () => setWsState('closed'));
    sock.on('room:joined', (msg) => {
      if (msg.role === 'candidate') setCandidateConnected(true);
    });
    sock.on('room:left', (msg) => {
      if (msg.role === 'candidate') {
        setCandidateConnected(false);
        // Clear in-flight pending so recruiter can re-ask after candidate rejoins.
        // Already-answered cards keep their badge (driven by `answers`, not by `pending`).
        setPending(null);
      }
    });
    sock.on('room:state', (msg) => {
      if (Array.isArray(msg.answers) && msg.answers.length) {
        setAnswers(msg.answers);
      }
    });
    sock.on('answer:submitted', (msg) => {
      setAnswers((prev) => {
        // dedupe by sequence_num
        if (prev.some((a) => a.sequence_num === msg.sequence_num)) return prev;
        return [...prev, msg];
      });
      setPending(null);
      // Phase 3.1: speak the answer aloud (en-US) — runs in parallel with the
      // visual badge. Web Speech API queues internally; back-to-back answers
      // play sequentially without overlap.
      speakAnswer(msg.answer);
    });
    sock.on('session:complete', (msg) => {
      setResult(msg.result);
      setAnswers(msg.answers || []);
      // Bump so AnalyticsCard debounces-refetches (350ms) — new metrics land
      // within ~1s of the candidate finishing.
      setAnalyticsRefreshSignal((n) => n + 1);
    });
    sock.connect();
  }, [recruiterSessionId]);

  const handleNewRoom = useCallback(async () => {
    // Phase 7 hard gate: Free plan caps at 3 rooms / month. Block the action
    // and surface the UpgradeModal. Initial dashboard mount also routes through
    // here, so a returning user at 3/3 sees the modal immediately.
    // Phase 15 (TRIAL_LIMIT_OVERRIDE) — bypass entirely when override is on.
    if (!unlimitedOverride && plan === 'free' && used >= 3) {
      openUpgrade('rooms_limit', 'hard');
      return;
    }
    setCreating(true);
    setError('');
    setAnswers([]);
    setResult(null);
    setPending(null);
    setCandidateConnected(false);
    try {
      // Close prior room if any
      if (room?.room_id) {
        closeRoom(room.room_id).catch(() => {});
      }
      const r = await createRoom(recruiterSessionId);
      setRoom(r);
      wireSocket(r.room_id);
      // Publish active room id for the global AgentAvatar popover.
      try { sessionStorage.setItem('talentconnect:active-room-id', r.room_id); } catch { /* noop */ }
      // Increment mock quota only AFTER a successful create.
      increment();
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setCreating(false);
    }
  }, [recruiterSessionId, room?.room_id, wireSocket, plan, used, openUpgrade, increment, unlimitedOverride]);

  // Phase 17 — CSV export handler for the unlocked "Export Results" card.
  // Reads from state already in this component (current-room `answers` +
  // `result` + `questions` catalog). No new fetch. Falls back to a single
  // demo row when nothing has been recorded yet.
  const [exportToast, setExportToast] = useState('');
  const exportToastTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(exportToastTimerRef.current), []);

  const handleExportCsv = useCallback(() => {
    const HEADER = 'Room_ID,Timestamp,Candidate_Status,Q1_Lift_25_50lbs,Q2_Shift_Avail,Q3_Steel_Toe_Boots';
    const csvEscape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const nowIso = new Date().toISOString();
    // Map fixed column positions Q1/Q2/Q3 → the questions catalog by index.
    const qIds = [questions[0]?.id, questions[1]?.id, questions[2]?.id];
    const findAns = (qid) => (answers.find((a) => a.question_id === qid)?.answer) || '';

    let rows;
    if (room?.room_id && answers.length > 0) {
      const status = (result && (result.status || result.outcome)) || 'in_progress';
      rows = [[
        csvEscape(room.room_id),
        csvEscape(nowIso),
        csvEscape(status),
        csvEscape(findAns(qIds[0])),
        csvEscape(findAns(qIds[1])),
        csvEscape(findAns(qIds[2])),
      ].join(',')];
    } else {
      // Demo fallback row per Phase 17 spec.
      rows = ['"demo-room-1","2025-04-01T10:00:00Z","qualified","yes","yes","yes"'];
    }
    const csv = `${HEADER}\n${rows.join('\n')}\n`;

    try {
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'talentconnect_report.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportToast('Report exported');
      clearTimeout(exportToastTimerRef.current);
      exportToastTimerRef.current = setTimeout(() => setExportToast(''), 1600);
    } catch { /* noop */ }
  }, [answers, questions, room, result]);

  const askQuestion = useCallback((q) => {
    if (!q || pending || result) return;
    const seq = answers.length + 1;
    const payload = {
      type: 'question:asked',
      question_id: q.id,
      question_text: q.text_en,
      asl_pattern: q.asl_pattern,
      asl_video_url: q.asl_video_url || null,
      sequence_num: seq,
    };
    if (sockRef.current?.send(payload)) {
      setPending(q.id);
    }
  }, [answers.length, pending, result]);

  // Phase 15 (REALTIME_QUESTION_INJECTION, 4/4) — the Assistant chat fires a
  // window CustomEvent when the recruiter injects a question. We relay it to
  // the current room's WS as `type: question_injection`. Room + QR stay
  // untouched. No DB write on server (backend just broadcasts to peers).
  useEffect(() => {
    const onInject = (ev) => {
      const question = ev?.detail?.question;
      if (!question || !sockRef.current || wsState !== 'open') return;
      // Sequence numbers for injected questions live above the fixed 3-slot
      // range (100+) so they don't collide with the qualification answers
      // stored in staffing_sessions.
      const seq = 100 + Date.now() % 1000;
      sockRef.current.send({
        type: 'question_injection',
        question_id: `inj-${Date.now()}`,
        question_text: String(question).slice(0, 400),
        asl_pattern: 'custom',
        sequence_num: seq,
      });
    };
    window.addEventListener('tc:inject-question', onInject);
    return () => window.removeEventListener('tc:inject-question', onInject);
  }, [wsState]);

  const candidateUrl = room
    ? `${window.location.origin}${room.candidate_path}`
    : '';

  const getAnswerFor = (qid) => answers.find((a) => a.question_id === qid);

  return (
    <div className="staffing-theme staffing-theme--with-topnav staffing-recruiter" data-testid="recruiter-dashboard">
      <div className="staffing-bg" aria-hidden="true" />

      {!TTS_SUPPORTED && (
        <div className="staffing-tts-warn" data-testid="tts-unsupported-warn">
          <VolumeX className="w-4 h-4" />
          <span>Audio output not supported in this browser. Visual badges still work.</span>
        </div>
      )}

      <header className="staffing-header">
        <Link to="/talent-connect" className="staffing-back-link" data-testid="back-to-staffing">← Talent Connect</Link>
        <div className="staffing-header-title">
          <span className="staffing-phase-tag">RECRUITER</span>
          <h1><span className="tc-upper">TALENT CONNECT</span>: <span className="tc-lower">your ai partner</span></h1>
        </div>
        <div className="staffing-header-right">
          <span className={`staffing-pill ${wsState === 'open' ? 'staffing-pill--ok' : 'staffing-pill--warn'}`} data-testid="ws-state-pill">
            {wsState === 'open' ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
            <span>WS · {wsState}</span>
          </span>
          <button
            className="staffing-btn staffing-btn--ghost"
            onClick={handleNewRoom}
            disabled={creating}
            data-testid="reset-room-btn"
          >
            <RotateCcw className="w-4 h-4" />
            <span>{creating ? 'Creating…' : 'New Room'}</span>
          </button>
        </div>
      </header>

      {error && (
        <div className="staffing-error" data-testid="recruiter-error">{error}</div>
      )}

      {plan === 'free' && !unlimitedOverride && (
        <div className="staffing-plan-banner-wrap">
          <PlanBanner used={used} onUpgrade={() => openUpgrade('rooms_limit', 'soft')} />
        </div>
      )}

      <main className="staffing-recruiter-main">
        {/* LEFT — QR + room id */}
        <section className="staffing-qr-panel staffing-glass" data-testid="qr-panel">
          <p className="staffing-eyebrow">Step 1 · Candidate scans</p>
          <h2 className="staffing-qr-heading">Scan to join</h2>

          <div className="staffing-qr-frame" data-testid="qr-frame">
            {room ? (
              <QRCode
                value={candidateUrl}
                size={260}
                bgColor="#020617"
                fgColor="#00f5ff"
                level="M"
              />
            ) : (
              <Loader2 className="w-12 h-12 animate-spin" />
            )}
          </div>

          {room && (
            <>
              <div className="staffing-room-id" data-testid="room-id">
                <span className="staffing-room-id-label">Room ID</span>
                <span className="staffing-room-id-value">{room.room_id}</span>
              </div>
              <div className="staffing-candidate-url-block">
                <span className="staffing-candidate-url-label">Or open this URL:</span>
                <code className="staffing-candidate-url" data-testid="candidate-url">{candidateUrl}</code>
              </div>
            </>
          )}

          <div
            className={`staffing-connect-status ${candidateConnected ? 'staffing-connect-status--on' : ''}`}
            data-testid="candidate-status"
          >
            <span className="staffing-status-dot" />
            <span>{candidateConnected ? 'Candidate connected' : 'Waiting for candidate…'}</span>
          </div>
        </section>

        {/* RIGHT — questions OR result */}
        <section className="staffing-questions-panel" data-testid="questions-panel">
          {result ? (
            <ResultScreen
              result={result}
              role="recruiter"
              answers={answers}
              questions={questions}
              onReset={handleNewRoom}
            />
          ) : (
            <>
              <p className="staffing-eyebrow">Step 2 · Tap to ask</p>
              <h2 className="staffing-questions-heading">3 Pre-qualification Questions</h2>

              {/* Waiting banner — fades out 200ms once candidate joins. Element stays in
                  the DOM so the transition runs both ways (join + later disconnect). */}
              <div
                className={`tc-waiting-banner ${!candidateConnected ? 'tc-waiting-banner--show' : ''}`}
                data-testid="recruiter-waiting-banner"
                aria-live="polite"
                role="status"
              >
                <Clock className="tc-waiting-icon" />
                <div className="tc-waiting-copy">
                  <p className="tc-waiting-title">Waiting for candidate to join…</p>
                  <p className="tc-waiting-sub">Share the QR code or the candidate URL to begin.</p>
                </div>
              </div>

              <div className="staffing-questions-list">
                {questions.map((q, i) => {
                  const ans = getAnswerFor(q.id);
                  const isPending = pending === q.id;
                  const lockedWaiting = !candidateConnected && !ans;
                  const disabled = lockedWaiting || !!pending || !!ans;
                  const iconCfg = ans ? ANSWER_ICON[ans.answer] : null;
                  return (
                    <button
                      key={q.id}
                      type="button"
                      onClick={() => askQuestion(q)}
                      disabled={disabled}
                      aria-disabled={disabled}
                      title={lockedWaiting ? 'Waiting for candidate to join' : undefined}
                      className={`staffing-question-card ${ans ? `staffing-question-card--${ans.answer}` : ''} ${isPending ? 'staffing-question-card--pending' : ''} ${lockedWaiting ? 'staffing-question-card--locked' : ''}`}
                      data-testid={`question-card-${q.id}`}
                      data-answered={ans ? ans.answer : 'none'}
                      data-locked={lockedWaiting ? 'true' : 'false'}
                    >
                      <span className="staffing-question-number">Q{i + 1}</span>
                      <div className="staffing-question-body">
                        <span className="staffing-question-text">{q.text_en}</span>
                        <span className="staffing-question-pattern">
                          ASL pattern · <code>{q.asl_pattern}</code>
                        </span>
                      </div>
                      <span className="staffing-question-state" data-testid={`question-state-${q.id}`}>
                        {ans && iconCfg ? (
                          <>
                            <iconCfg.Icon size={28} style={{ color: iconCfg.color }} />
                            <span style={{ color: iconCfg.color }}>{iconCfg.label}</span>
                          </>
                        ) : isPending ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Awaiting…</span>
                          </>
                        ) : (
                          <>
                            <ChevronRight size={22} />
                            <span>Tap to ask</span>
                          </>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="staffing-recruiter-preview">
                <p className="staffing-eyebrow mb-2">Live ASL preview</p>
                <AslSphere
                  pattern={pending ? (questions.find((q) => q.id === pending)?.asl_pattern || 'idle') : 'idle'}
                  size={120}
                />
              </div>
            </>
          )}
        </section>

        {/* Phase 7 — Business preview strip (mocked locks; clicks open UpgradeModal) */}
        {/* Phase 11 — Analytics slot is now a live component (free = preview, business = full metrics) */}
        <section className="tc-locked-grid" data-testid="locked-features">
          <AnalyticsCard
            recruiterSessionId={recruiterSessionId}
            plan={unlimitedOverride ? 'business' : plan}
            refreshSignal={analyticsRefreshSignal}
            onUpgradeClick={(feature) => openUpgrade(feature)}
          />

          <div
            className="tc-locked-card"
            data-testid="analytics-export-card"
            style={{
              cursor: 'default',
              opacity: 1,
              alignItems: 'flex-start',
              gap: 6,
            }}
          >
            <Download className="tc-locked-icon" />
            <span className="tc-locked-title">Export Results</span>
            <span className="tc-locked-hint">CSV · PDF · per-room</span>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                type="button"
                onClick={handleExportCsv}
                data-testid="analytics-export-csv"
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  background: '#7F00FF',
                  color: '#F8FAFC',
                  border: '1px solid rgba(127, 0, 255, 0.6)',
                  cursor: 'pointer',
                }}
              >
                Download CSV
              </button>
              <button
                type="button"
                disabled
                title="Coming soon"
                data-testid="analytics-export-pdf"
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  background: 'rgba(127, 0, 255, 0.12)',
                  color: '#F8FAFC',
                  border: '1px solid rgba(127, 0, 255, 0.3)',
                  cursor: 'not-allowed',
                  opacity: 0.5,
                }}
              >
                PDF
              </button>
            </div>
            {exportToast && (
              <span
                role="status"
                aria-live="polite"
                data-testid="analytics-export-toast"
                style={{ marginTop: 6, fontSize: 11, color: '#7F00FF' }}
              >
                {exportToast}
              </span>
            )}
          </div>

          <button
            type="button"
            className="tc-locked-card"
            onClick={() => openUpgrade('custom_questions')}
            data-testid="locked-custom-questions"
          >
            <span className="tc-locked-badge"><Lock className="w-3 h-3" /></span>
            <Plus className="tc-locked-icon" />
            <span className="tc-locked-title">Add Custom Question</span>
            <span className="tc-locked-hint">Replace defaults with up to 10</span>
          </button>

          <button
            type="button"
            className="tc-locked-card"
            onClick={() => openUpgrade('branding')}
            data-testid="locked-branding"
          >
            <span className="tc-locked-badge"><Lock className="w-3 h-3" /></span>
            <Paintbrush className="tc-locked-icon" />
            <span className="tc-locked-title">Custom Branding</span>
            <span className="tc-locked-hint">Your logo · colors</span>
          </button>
        </section>

        <div className="tc-recruiter-footer-cta" data-testid="recruiter-pricing-link">
          <Link to="/talent-connect/pricing" className="tc-mini-link">
            <Sparkles className="w-3 h-3" />
            <span>View Pricing</span>
          </Link>
        </div>
      </main>
      <AppFooter />

      {IS_DEV_BUILD && (
        <DevTools />
      )}

      <UpgradeModal
        isOpen={upgradeModal.open}
        onClose={closeUpgrade}
        feature={upgradeModal.feature}
        variant={upgradeModal.variant}
      />
    </div>
  );
}

function DevTools() {
  const handleReset = () => {
    try {
      localStorage.removeItem('talentconnect:mock-plan');
      localStorage.removeItem('talentconnect:mock-rooms-used');
    } catch { /* noop */ }
    window.location.reload();
  };
  const handleToggle = () => {
    const next = toggleMockPlan();
    // Full reload so the AnalyticsCard fully remounts with the new plan.
    // The card would re-fetch anyway from the useMockPlan hook, but reload
    // also refreshes the PlanBanner + locked-features layout in one shot.
    window.location.reload();
    return next;
  };
  return (
    <div className="tc-dev-tools" data-testid="dev-tools">
      <button
        type="button"
        onClick={handleReset}
        className="tc-dev-reset"
        data-testid="dev-reset-plan"
        title="Dev only — clear the mocked Talent Connect plan + quota and reload"
      >
        [DEV] Reset mock plan
      </button>
      <button
        type="button"
        onClick={handleToggle}
        className="tc-dev-reset"
        data-testid="dev-toggle-plan"
        title="Dev only — flip between Free and Business to view both analytics states"
      >
        [DEV] Toggle plan: Free ↔ Business
      </button>
    </div>
  );
}
